import type {
  AssistantActionPlan,
  AssistantChatAttachment,
  AssistantChatMessage,
  AssistantSession,
  AssistantSessionSearchResult,
} from '@/lib/api/assistant.api'
import type { CompanionPageContext } from '@/lib/benny-companion/events'
import type { BennyToolCallState } from '@/store/benny.store'
import {
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  sanitizeStreamingContent,
  toClipboardText,
} from '@/components/assistant/message-content'
import { HelpDrawer } from '@/components/help/HelpDrawer'
import { HelpPlanBadge } from '@/components/help/HelpPlanBadge'
import { useHelpVisibility } from '@/components/help/useHelpVisibility'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { UserAvatar } from '@/components/ui/user-avatar'
import {
  useAssistantInvalidation,
  useAssistantMessagesQuery,
  useAssistantSessionsQuery,
} from '@/hooks/queries/useAssistantQuery'
import { useIsMobile } from '@/hooks/use-mobile'
import { useAICredits } from '@/hooks/useAICredits'
import { assistantApi } from '@/lib/api/assistant.api'
import { helpApi } from '@/lib/api/help.api'
import { cn } from '@/lib/utils'
import { useAuth } from '@/store/auth.store'
import { useBennyStore } from '@/store/benny.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'
import {
  ArrowClockwise,
  ArrowElbowDownLeft,
  ArrowsInSimple,
  ArrowsOutSimple,
  ArrowSquareOut,
  BookOpenText,
  ChatCircleDots,
  Check,
  Copy,
  Image as ImageIcon,
  MagnifyingGlass,
  Paperclip,
  PaperPlaneRight,
  Plus,
  Stop,
  Trash,
  WarningCircle,
  X,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import { useLocation, useMatches, useParams } from 'react-router-dom'
import rehypeHighlight from 'rehype-highlight'
import { toast } from 'sonner'

import type { AppPreferences } from '@scrambl/shared'

import type { PendingAttachment } from './chat-attachments'
import { AssistantAppLink, useAssistantNavigation } from './AssistantNavigation'
import { acceptAttachmentFiles, MAX_ATTACHMENTS } from './chat-attachments'
import {
  assistantUrlTransform,
  ModuleCardList,
  ModuleInlineChip,
  normalizeAssistantCardsForContext,
  parseScramblCards,
  parseScramblFollowups,
  parseScramblLink,
} from './ModuleCards'

import './benny-chat.css'
import { getModifierKeyLabel } from '@/utils/keyboard-utils'

// Module-level so the array identity is stable across renders — passing a
// fresh `[rehypeHighlight]` literal every render makes ReactMarkdown rebuild
// its plugin pipeline and re-process the markdown tree.
const rehypePlugins = [rehypeHighlight]

const QUICK_PROMPTS = [
  { icon: ArrowElbowDownLeft, text: 'Show my top priorities for today' },
  { icon: ArrowElbowDownLeft, text: 'Summarize what changed in this project' },
  { icon: ArrowElbowDownLeft, text: 'Create a task list based on this page' },
]

function buildAiContext(
  params: Record<string, string | undefined>,
  search: string,
) {
  const searchParams = new URLSearchParams(search)
  const taskId = params.taskId || searchParams.get('task') || undefined
  const projectId =
    params.projectId || searchParams.get('projectId') || undefined
  const entityId = params.entityId || searchParams.get('entityId') || undefined
  const parentTaskId =
    params.parentTaskId || searchParams.get('parentTaskId') || undefined

  const extraIds: Record<string, string | undefined> = {
    noteId: params.noteId,
    documentId: params.documentId,
    contractId: params.contractId,
    invoiceId: params.id,
    leadId: params.leadId,
    dealId: params.dealId,
    formId: params.formId,
  }

  const extraParts = Object.entries(extraIds)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}: ${value}`)

  const additionalContext =
    extraParts.length > 0
      ? `Page context IDs: ${extraParts.join(', ')}`
      : undefined

  return {
    ...(projectId ? { projectId } : {}),
    ...(entityId ? { entityId } : {}),
    ...(taskId ? { taskId } : {}),
    ...(parentTaskId ? { parentTaskId } : {}),
    ...(additionalContext ? { additionalContext } : {}),
  }
}

function buildPageContext(
  pathname: string,
  search: string,
  hash: string,
  params: Record<string, string | undefined>,
) {
  return {
    pathname,
    search,
    hash,
    title: document.title,
    params,
  }
}

export function BennyChat({
  variant = 'app',
  onClose: onCloseProp,
  remoteContext,
  onHeaderPointerDown,
}: {
  /**
   * 'app' — the docked workspace panel inside the main window (default).
   * 'companion' — fills its parent container inside the standalone desktop
   * companion window: always open, no preference gating, no maximize chrome.
   */
  variant?: 'app' | 'companion'
  /** Companion mode: called when the user closes the chat panel. */
  onClose?: () => void
  /**
   * Companion mode: the main window's current route context, streamed over
   * Tauri events. Used instead of this window's own location (which is just
   * /benny-companion) so Benny knows what the user is looking at and can act
   * on it (update this task, summarize this project, ...).
   */
  remoteContext?: CompanionPageContext | null
  /**
   * Companion mode: pointer-down on the header's non-interactive area —
   * used to drag the companion window by the chat header.
   */
  onHeaderPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void
} = {}) {
  const isCompanion = variant === 'companion'
  const storeOpen = useBennyStore(s => s.open)
  const open = isCompanion || storeOpen
  const setOpen = useBennyStore(s => s.setOpen)
  const maximized = useBennyStore(s => s.maximized)
  const setMaximized = useBennyStore(s => s.setMaximized)
  const streamingState = useBennyStore(s => s.streamingState)
  const startStream = useBennyStore(s => s.startStream)
  const appendDelta = useBennyStore(s => s.appendDelta)
  const startToolCall = useBennyStore(s => s.startToolCall)
  const endToolCall = useBennyStore(s => s.endToolCall)
  const endStream = useBennyStore(s => s.endStream)
  const cancelStream = useBennyStore(s => s.cancelStream)
  const pendingPrompt = useBennyStore(s => s.pendingPrompt)
  const consumePendingPrompt = useBennyStore(s => s.consumePendingPrompt)
  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [optimisticMessages, setOptimisticMessages] = useState<
    AssistantChatMessage[]
  >([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Surfaces like Guided Setup queue a suggested prompt via
  // benny.store.openWithPrompt — copy it into the input once; never auto-send.
  useEffect(() => {
    if (!pendingPrompt) return
    const prompt = consumePendingPrompt()
    if (prompt) setInput(prompt)
  }, [pendingPrompt, consumePendingPrompt])
  const [dailyLimitResetsAt, setDailyLimitResetsAt] = useState<string | null>(
    null,
  )
  const [actionPlan, setActionPlan] = useState<AssistantActionPlan | null>(null)
  const [isPlanning, setIsPlanning] = useState(false)
  const [isExecutingPlan, setIsExecutingPlan] = useState(false)
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const [isDropping, setIsDropping] = useState(false)
  // Track the in-flight stream's abort controller so we can cancel on
  // unmount — otherwise navigating away mid-stream leaves the request
  // running on the server until OpenAI replies.
  const inFlightAbortRef = useRef<AbortController | null>(null)
  const [isSessionPickerOpen, setIsSessionPickerOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameSessionId, setRenameSessionId] = useState<string | null>(null)
  const [renameTitle, setRenameTitle] = useState('')
  const [isRenamingSession, setIsRenamingSession] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null)
  const [isDeletingSession, setIsDeletingSession] = useState(false)
  const [selectedHelpId, setSelectedHelpId] = useState<string | null>(null)
  const forwardNavigate = useAssistantNavigation()
  const [hasInitialized, setHasInitialized] = useState(false)
  const { data: planCreditStatus, refetch: refetchPlanCredits } = useAICredits(
    'assistant_action_plan',
    open,
  )
  const isMobile = useIsMobile()
  const location = useLocation()
  const matches = useMatches()
  const storedPreferences = useUserPreferencesStore(s => s.preferences)
  const prefsInitialized = useUserPreferencesStore(s => s.isInitialized)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const bennyAppPref = getUserAppPreference(storedPreferences)
  const bennyEnabled =
    prefsInitialized &&
    (bennyAppPref?.preferences as AppPreferences | undefined)?.bennyEnabled ===
      true
  const bennyCompanionEnabled =
    prefsInitialized &&
    (bennyAppPref?.preferences as AppPreferences | undefined)
      ?.bennyCompanionEnabled === true
  const isHelpRoute = /(^|\/)help(\/|$)/.test(location.pathname)
  // Companion mode is gated by the companion window's own boot flow, not by
  // synced preferences (which are never initialized in that window).
  const showBennyChat =
    isCompanion || bennyEnabled || bennyCompanionEnabled || isHelpRoute
  const showEnableBennyHint = prefsInitialized && isHelpRoute && !bennyEnabled
  const isDailyLimitReached =
    dailyLimitResetsAt !== null &&
    new Date(dailyLimitResetsAt).getTime() > Date.now()

  useEffect(() => {
    if (!dailyLimitResetsAt) return undefined
    const delay = new Date(dailyLimitResetsAt).getTime() - Date.now()
    if (delay <= 0) {
      setDailyLimitResetsAt(null)
      return undefined
    }
    const timer = window.setTimeout(() => setDailyLimitResetsAt(null), delay)
    return () => window.clearTimeout(timer)
  }, [dailyLimitResetsAt])

  const localParams = useMemo(
    () =>
      matches.reduce<Record<string, string | undefined>>((acc, match) => {
        return { ...acc, ...(match.params || {}) }
      }, {}),
    [matches],
  )

  // Companion mode: build context from the main window's streamed route —
  // this window's own route is always /benny-companion and carries nothing.
  const params = remoteContext?.params ?? localParams
  const contextSearch = remoteContext ? remoteContext.search : location.search

  const aiContext = useMemo(
    () => buildAiContext(params, contextSearch),
    [params, contextSearch],
  )
  const pageContext = useMemo(
    () =>
      remoteContext
        ? {
            pathname: remoteContext.pathname,
            search: remoteContext.search,
            hash: remoteContext.hash,
            title: remoteContext.title,
            params: remoteContext.params,
          }
        : buildPageContext(
            location.pathname,
            location.search,
            location.hash,
            params,
          ),
    [remoteContext, location.pathname, location.search, location.hash, params],
  )

  // React Query: sessions list (cached 5min)
  const { data: sessions = [], isLoading: isSessionsLoading } =
    useAssistantSessionsQuery({ enabled: open })

  // React Query: messages for active session (cached 10min)
  const {
    data: messagePages,
    isLoading: isMessagesLoading,
    hasNextPage: hasOlderMessages,
    fetchNextPage: fetchOlderMessages,
    isFetchingNextPage: isFetchingOlderMessages,
  } = useAssistantMessagesQuery(sessionId, {
    enabled: open && !!sessionId,
  })

  const cachedMessages = useMemo(
    () =>
      messagePages
        ? [...messagePages.pages].reverse().flatMap(page => page.items)
        : undefined,
    [messagePages],
  )

  const {
    invalidateSessions,
    setMessages: setCachedMessages,
    removeSession,
  } = useAssistantInvalidation()

  // Merge cached messages with optimistic + streaming messages. The streaming
  // assistant bubble lives in benny.store and renders directly from
  // `streamingState` until message_end fires; at that point React Query
  // takes over with the persisted version.
  // Memoized so unrelated re-renders (e.g. typing in the input) don't produce
  // a new array reference and retrigger downstream effects like auto-scroll.
  const messages = useMemo(() => {
    const base = cachedMessages ?? []
    const baseIds = new Set(base.map(m => m.id))
    const extras = optimisticMessages.filter(m => !baseIds.has(m.id))
    const merged: AssistantChatMessage[] = [...base, ...extras]

    if (streamingState && !baseIds.has(streamingState.messageId)) {
      merged.push({
        id: streamingState.messageId,
        role: 'assistant',
        // Hide half-streamed machine blocks (scrambl-cards JSON etc.) —
        // they render as cards only once their closing fence arrives.
        content: sanitizeStreamingContent(streamingState.content || ''),
        toolName: null,
        toolCallId: null,
        createdAt: new Date().toISOString(),
      })
    }
    return merged
  }, [cachedMessages, optimisticMessages, streamingState])

  const handleLoadOlderMessages = useCallback(async () => {
    if (!hasOlderMessages || isFetchingOlderMessages) return
    const viewport = scrollRef.current
    const previousHeight = viewport?.scrollHeight ?? 0
    const previousTop = viewport?.scrollTop ?? 0

    await fetchOlderMessages()

    requestAnimationFrame(() => {
      if (!viewport) return
      const addedHeight = viewport.scrollHeight - previousHeight
      viewport.scrollTop = previousTop + Math.max(0, addedHeight)
    })
  }, [fetchOlderMessages, hasOlderMessages, isFetchingOlderMessages])

  // Auto-select latest session on first open
  useEffect(() => {
    if (!open || hasInitialized || isSessionsLoading) return
    setHasInitialized(true)
    if (sessions.length > 0 && !sessionId) {
      setSessionId(sessions[0].id)
    }
  }, [open, hasInitialized, isSessionsLoading, sessions, sessionId])

  // Reset state when panel closes
  useEffect(() => {
    if (!open) {
      setHasInitialized(false)
    }
  }, [open])

  const activeSession = useMemo(
    () => sessions.find(session => session.id === sessionId),
    [sessions, sessionId],
  )
  const activeSessionLabel = (() => {
    if (!sessionId) return 'New chat'
    const title = activeSession?.title?.trim()
    return title && title.length > 0 ? title : 'Current chat'
  })()

  const isHistoryLoading = isMessagesLoading && !!sessionId

  // "Pinned" = the user is at (or near) the bottom and wants to follow new
  // content. Scrolling up unpins; scrolling back down re-pins. Programmatic
  // jumps land at distance ~0, so they keep the flag true.
  const pinnedToBottomRef = useRef(true)

  useEffect(() => {
    if (!open) return undefined
    pinnedToBottomRef.current = true
    const el = scrollRef.current
    if (!el) return undefined
    const handleScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight
      pinnedToBottomRef.current = distance < 80
    }
    el.addEventListener('scroll', handleScroll, { passive: true })
    return () => el.removeEventListener('scroll', handleScroll)
  }, [open])

  // Keep the view glued to the bottom while pinned. A ResizeObserver on the
  // scroll content catches every async height change — history rendering on
  // open, streaming deltas, card lists animating in, the stream→cache
  // handoff — which a messages-effect alone misses (content keeps growing
  // after the effect ran, leaving the view stranded mid-scroll).
  useEffect(() => {
    if (!open) return undefined
    const el = scrollRef.current
    const content = el?.firstElementChild
    if (!el || !content) return undefined
    const observer = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) {
        el.scrollTop = el.scrollHeight
      }
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [open])

  // On open (and once history finishes loading), jump straight to the bottom.
  // Instant, not smooth — queued smooth scrolls fight each other and jitter.
  useEffect(() => {
    if (!open || isHistoryLoading) return
    const el = scrollRef.current
    if (!el) return
    pinnedToBottomRef.current = true
    // rAF lets the popover/sheet mount and lay out before we measure height.
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight
    })
    return () => cancelAnimationFrame(raf)
  }, [open, isHistoryLoading])

  // Follow new messages/deltas while pinned (instant — see above).
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !pinnedToBottomRef.current) return
    el.scrollTop = el.scrollHeight
  }, [
    messages,
    streamingState?.content,
    streamingState?.toolCalls.length,
    open,
  ])

  // Keyboard shortcut: Cmd+. (Mac) / Ctrl+. (Windows) to toggle chat.
  // Not wired in companion mode — the companion window controls visibility.
  useEffect(() => {
    if (isCompanion) return undefined
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        if (!showBennyChat) return
        e.preventDefault()
        setOpen(!open)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, setOpen, showBennyChat, isCompanion])

  useEffect(() => {
    if (!open) return
    // Delay to allow popover/sheet to mount and render the input
    const timer = setTimeout(() => inputRef.current?.focus(), 150)
    return () => clearTimeout(timer)
  }, [open])

  // Add files (from picker, drop, or paste). The header composer stays
  // image-only — Simple Mode is the surface that also takes documents.
  const addFiles = async (files: File[] | FileList) => {
    const { accepted, error } = await acceptAttachmentFiles(
      files,
      attachments,
      'image',
    )
    if (error) toast.error(error)
    if (accepted.length > 0) {
      setAttachments(prev => [...prev, ...accepted].slice(0, MAX_ATTACHMENTS))
    }
  }

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const target = prev.find(a => a.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter(a => a.id !== id)
    })
  }

  const handleCreateActionPlan = useCallback(
    async (instruction: string) => {
      const trimmed = instruction.replace(/^plan:\s*/i, '').trim()
      if (!trimmed || isPlanning || isLoading) return
      if (planCreditStatus?.canUse === false) {
        toast.error('Not enough AI credits to build an action plan.')
        return
      }
      setIsPlanning(true)
      try {
        const plan = await assistantApi.createActionPlan({
          instruction: trimmed,
          context: { ...aiContext, pageContext },
        })
        setActionPlan(plan)
        void refetchPlanCredits()
      } catch (error: any) {
        toast.error(error?.message || 'Benny could not build that action plan.')
        void refetchPlanCredits()
      } finally {
        setIsPlanning(false)
      }
    },
    [
      aiContext,
      isLoading,
      isPlanning,
      pageContext,
      planCreditStatus?.canUse,
      refetchPlanCredits,
    ],
  )

  const handleExecuteActionPlan = useCallback(async () => {
    if (!actionPlan?.executable || isExecutingPlan) return
    setIsExecutingPlan(true)
    try {
      const result = await assistantApi.executeActionPlan(actionPlan.planId)
      setActionPlan(current =>
        current ? { ...current, status: result.status } : current,
      )
      if (result.status === 'completed') {
        toast.success('Benny completed the approved plan.')
      } else {
        toast.error('Benny stopped when a plan step failed.')
      }
    } catch (error: any) {
      toast.error(error?.message || 'The action plan could not be executed.')
    } finally {
      setIsExecutingPlan(false)
    }
  }, [actionPlan, isExecutingPlan])

  // Revoke object URLs and abort any in-flight stream on unmount to avoid
  // leaking blobs and keeping the server busy after the user navigates away.
  useEffect(() => {
    return () => {
      setAttachments(prev => {
        prev.forEach(a => URL.revokeObjectURL(a.previewUrl))
        return []
      })
      if (inFlightAbortRef.current) {
        inFlightAbortRef.current.abort()
        inFlightAbortRef.current = null
      }
    }
  }, [])

  const handleSend = useCallback(
    async (message: string) => {
      const trimmedRaw = message.trim()
      if (/^plan:\s*/i.test(trimmedRaw)) {
        setInput('')
        await handleCreateActionPlan(trimmedRaw)
        return
      }
      // Allow sending if there's text OR at least one attachment.
      if (isLoading) return
      if (isDailyLimitReached) return
      if (!trimmedRaw && attachments.length === 0) return
      // Backend requires a non-empty message field — synthesize a brief
      // placeholder when the user only sent images.
      const trimmed = trimmedRaw || (attachments.length > 0 ? '(image)' : '')

      const tempId = `temp-${Date.now()}`
      const tempMsg: AssistantChatMessage = {
        id: tempId,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      }
      setOptimisticMessages(prev => [...prev, tempMsg])
      setInput('')
      setIsLoading(true)

      const abort = new AbortController()
      inFlightAbortRef.current = abort
      let resolvedSessionId: string | undefined = sessionId
      let didStart = false
      // The streaming messageId owned by THIS turn — passed to endStream so a
      // slow finally can't wipe a newer turn's stream.
      let streamMessageId: string | null = null
      const requestAttachments: AssistantChatAttachment[] = attachments.map(
        a => ({
          contentType: a.contentType,
          base64: a.base64,
          fileName: a.fileName,
        }),
      )
      // Clear staged attachments + revoke previews before the request returns —
      // the backend now owns them. If the request fails we don't auto-restore;
      // the user can re-attach if they want to retry.
      if (attachments.length > 0) {
        attachments.forEach(a => URL.revokeObjectURL(a.previewUrl))
        setAttachments([])
      }

      try {
        await assistantApi.chatStream(
          {
            message: trimmed,
            sessionId,
            context: aiContext,
            pageContext,
            attachments:
              requestAttachments.length > 0 ? requestAttachments : undefined,
          },
          {
            signal: abort.signal,
            onEvent: event => {
              switch (event.type) {
                case 'session':
                  resolvedSessionId = event.sessionId
                  setSessionId(event.sessionId)
                  break
                case 'user_message':
                  // Replace temp user message with the persisted one so
                  // subsequent renders use real ids.
                  setOptimisticMessages(prev =>
                    prev.map(m =>
                      m.id === tempId
                        ? { ...m, id: event.id, createdAt: event.createdAt }
                        : m,
                    ),
                  )
                  break
                case 'message_start':
                  didStart = true
                  streamMessageId = event.messageId
                  startStream(event.messageId, abort)
                  break
                case 'content_delta':
                  appendDelta(event.messageId, event.delta)
                  break
                case 'tool_call_start':
                  startToolCall(event.toolCallId, event.toolName)
                  break
                case 'tool_call_end':
                  endToolCall(
                    event.toolCallId,
                    event.status,
                    event.durationMs,
                    event.error,
                  )
                  break
                case 'message_end':
                  // Stream finished — let React Query refetch the canonical
                  // messages so cached state matches DB exactly.
                  break
                case 'error':
                  // Provider failures are persisted as an assistant turn and
                  // render as an error card in the thread — a toast saying the
                  // same thing twice is just noise.
                  if (!isInThreadErrorCode(event.code)) {
                    toast.error(event.message)
                  }
                  if (event.code === 'BENNY_DAILY_LIMIT_REACHED') {
                    setDailyLimitResetsAt(
                      event.resetsAt ??
                        new Date(
                          Date.UTC(
                            new Date().getUTCFullYear(),
                            new Date().getUTCMonth(),
                            new Date().getUTCDate() + 1,
                          ),
                        ).toISOString(),
                    )
                    setOptimisticMessages(prev =>
                      prev.filter(m => m.id !== tempId),
                    )
                    setInput(trimmedRaw)
                  }
                  break
                case 'done':
                  break
              }
            },
          },
        )
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          // Clear this turn's streaming bubble immediately so Stop feels
          // responsive instead of lingering until the finally's refetch
          // resolves. The later endStream in `finally` is a harmless no-op.
          endStream(streamMessageId ?? undefined)
        } else {
          if (!didStart) {
            // Stream never started — drop the optimistic user message so the
            // user can retry without seeing a duplicate.
            setOptimisticMessages(prev => prev.filter(m => m.id !== tempId))
          }
          toast.error(
            error instanceof Error
              ? error.message
              : 'Something went wrong. Please try again.',
          )
        }
      } finally {
        setIsLoading(false)
        if (inFlightAbortRef.current === abort) inFlightAbortRef.current = null
        if (resolvedSessionId) {
          // Pull the canonical message list from the server so streamed
          // content + tool calls land in the React Query cache identically
          // to a non-streaming response.
          try {
            const fresh = await assistantApi.getMessages(resolvedSessionId)
            setCachedMessages(resolvedSessionId, fresh)
            setOptimisticMessages([])
            void invalidateSessions()
          } catch {
            // If the refetch fails, leave the optimistic state in place; the
            // user can refresh manually.
          }
        }
        // Clear the streaming bubble only AFTER the canonical messages are in
        // the cache. The merge in `messages` skips the streaming copy once the
        // cached list contains the same id, so the handoff is seamless —
        // clearing first left a visible gap (the response "flickered" away
        // until the refetch landed). Pass this turn's messageId so we never
        // wipe a newer turn's stream that started while we awaited above.
        endStream(streamMessageId ?? undefined)
      }
    },
    [
      aiContext,
      appendDelta,
      attachments,
      endStream,
      endToolCall,
      invalidateSessions,
      isDailyLimitReached,
      isLoading,
      handleCreateActionPlan,
      pageContext,
      sessionId,
      setCachedMessages,
      startStream,
      startToolCall,
    ],
  )

  const handleNewChat = () => {
    setSessionId(undefined)
    setOptimisticMessages([])
  }

  const handleSelectSession = (session: AssistantSession) => {
    setIsSessionPickerOpen(false)
    setSessionId(session.id)
    setOptimisticMessages([])
  }

  const handleRenameSession = async () => {
    if (!renameSessionId) return
    const nextTitle = renameTitle.trim()
    if (!nextTitle) {
      toast.error('Enter a session title to rename.')
      return
    }
    setIsRenamingSession(true)
    try {
      await assistantApi.renameSession(renameSessionId, nextTitle)
      void invalidateSessions()
      setRenameDialogOpen(false)
    } catch (error) {
      toast.error('Unable to rename session.')
    } finally {
      setIsRenamingSession(false)
    }
  }

  const handleDeleteSession = async () => {
    if (!deleteSessionId) return
    setIsDeletingSession(true)
    try {
      await assistantApi.deleteSession(deleteSessionId)
      removeSession(deleteSessionId)
      if (deleteSessionId === sessionId) {
        handleNewChat()
      }
    } catch (error) {
      toast.error('Unable to delete session.')
    } finally {
      setIsDeletingSession(false)
      setDeleteSessionId(null)
    }
  }

  const openRenameDialog = (session: AssistantSession) => {
    setRenameSessionId(session.id)
    setRenameTitle(session.title ?? '')
    setRenameDialogOpen(true)
    setIsSessionPickerOpen(false)
  }

  const openDeleteDialog = (session: AssistantSession) => {
    setDeleteSessionId(session.id)
    setDeleteDialogOpen(true)
    setIsSessionPickerOpen(false)
  }

  const handleOpenHelpCitation = useCallback(
    (helpId: string) => {
      if (forwardNavigate) {
        forwardNavigate(`/help/articles/${encodeURIComponent(helpId)}`)
        return
      }
      setSelectedHelpId(helpId)
    },
    [forwardNavigate],
  )

  const chatInner = (mode: 'popover' | 'sheet' | 'companion') => (
    <BennyChatInner
      mode={mode}
      messages={messages}
      isLoading={isLoading}
      isHistoryLoading={isHistoryLoading}
      input={input}
      setInput={setInput}
      handleSend={handleSend}
      onExpand={() => setMaximized(true)}
      onCollapse={() => setMaximized(false)}
      onClose={() => (isCompanion ? onCloseProp?.() : setOpen(false))}
      scrollRef={scrollRef}
      inputRef={inputRef}
      isStreaming={!!streamingState}
      onStop={cancelStream}
      isMobile={isCompanion ? false : isMobile}
      onNewChat={handleNewChat}
      attachments={attachments}
      onAddFiles={addFiles}
      onRemoveAttachment={removeAttachment}
      isDropping={isDropping}
      onSetDropping={setIsDropping}
      onOpenSessions={() => setIsSessionPickerOpen(true)}
      onOpenHelpCitation={handleOpenHelpCitation}
      showEnableBennyHint={showEnableBennyHint}
      actionPlan={actionPlan}
      isPlanning={isPlanning}
      isExecutingPlan={isExecutingPlan}
      onExecutePlan={() => void handleExecuteActionPlan()}
      onDismissPlan={() => setActionPlan(null)}
      onEditPlan={instruction => {
        setActionPlan(null)
        setInput(`Plan: ${instruction}`)
        setTimeout(() => inputRef.current?.focus(), 0)
      }}
      hasOlderMessages={hasOlderMessages}
      isLoadingOlderMessages={isFetchingOlderMessages}
      onLoadOlderMessages={() => void handleLoadOlderMessages()}
      onHeaderPointerDown={onHeaderPointerDown}
      isDailyLimitReached={isDailyLimitReached}
    />
  )

  return (
    <>
      {/* Companion window: the chat simply fills its parent container —
          the companion page owns the window chrome, sizing, and close. */}
      {isCompanion && (
        <div className="flex h-full min-h-0 w-full flex-col">
          {chatInner('companion')}
        </div>
      )}

      {/* Mobile: always full-screen sheet. Popover/maximize toggle isn't
          useful on small screens — it just produces a tiny floating box that
          fights the on-screen keyboard. The `.benny-mobile-sheet` CSS class
          forces full viewport width and height; we don't pass defaultWidth
          here on purpose so the size is driven entirely from CSS. */}
      {!isCompanion && open && showBennyChat && isMobile && (
        <Sheet open onOpenChange={setOpen}>
          <SheetContent
            side="right"
            className="benny-mobile-sheet p-0"
            hideCloseButton
          >
            {chatInner('sheet')}
          </SheetContent>
        </Sheet>
      )}

      {/* Desktop: persistent workspace panel. It is mounted beside the main
          page in MainLayout, so routes remain visible and navigable while
          Benny is open. Expanding changes the dock width without creating a
          modal overlay. */}
      {!isCompanion && open && showBennyChat && !isMobile && (
        <BennyDockedPanel expanded={maximized}>
          {chatInner(maximized ? 'sheet' : 'popover')}
        </BennyDockedPanel>
      )}

      {/* Sessions picker / search dialog */}
      <SessionPickerDialog
        open={isSessionPickerOpen}
        onOpenChange={setIsSessionPickerOpen}
        sessions={sessions}
        activeSessionId={sessionId}
        onSelectSession={handleSelectSession}
        onNewChat={() => {
          setIsSessionPickerOpen(false)
          handleNewChat()
        }}
        onRename={openRenameDialog}
        onDelete={openDeleteDialog}
      />

      {/* Dialogs — rendered outside both containers */}
      <Dialog
        open={renameDialogOpen}
        onOpenChange={nextOpen => {
          setRenameDialogOpen(nextOpen)
          if (!nextOpen) {
            setRenameSessionId(null)
            setRenameTitle('')
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Rename chat</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-2">
            <Input
              value={renameTitle}
              onChange={event => setRenameTitle(event.target.value)}
              placeholder="Session title"
              autoFocus
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              onClick={() => setRenameDialogOpen(false)}
              disabled={isRenamingSession}
            >
              Cancel
            </Button>
            <Button onClick={handleRenameSession} disabled={isRenamingSession}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={nextOpen => {
          setDeleteDialogOpen(nextOpen)
          if (!nextOpen) {
            setDeleteSessionId(null)
          }
        }}
        title="Delete this chat?"
        description="This will permanently delete the selected session and its messages."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteSession}
        isLoading={isDeletingSession}
        variant="destructive"
      />
      {!isCompanion && (
        <HelpDrawer
          helpId={selectedHelpId}
          open={!!selectedHelpId}
          onOpenChange={nextOpen => {
            if (!nextOpen) setSelectedHelpId(null)
          }}
        />
      )}
    </>
  )
}

/**
 * Dialog that lists recent chat sessions and lets the user search across
 * all past messages. Search is debounced and hits
 * `GET /ai/assistant/sessions/search?q=…` returning sessions ranked by
 * recency with a snippet from the matching message.
 */
function SessionPickerDialog({
  open,
  onOpenChange,
  sessions,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onRename,
  onDelete,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  sessions: AssistantSession[]
  activeSessionId: string | undefined
  onSelectSession: (session: AssistantSession) => void
  onNewChat: () => void
  onRename: (session: AssistantSession) => void
  onDelete: (session: AssistantSession) => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AssistantSessionSearchResult[] | null>(
    null,
  )
  const [isSearching, setIsSearching] = useState(false)

  // Reset query when the dialog closes so reopening shows the default list.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setResults(null)
    }
  }, [open])

  // Debounced search.
  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed) {
      setResults(null)
      setIsSearching(false)
      return
    }
    const handle = setTimeout(async () => {
      setIsSearching(true)
      try {
        const res = await assistantApi.searchSessions(trimmed)
        setResults(res)
      } catch {
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 200)
    return () => clearTimeout(handle)
  }, [query])

  // The list to render: search results when actively searching, otherwise
  // the recent sessions list.
  const showResults = query.trim().length > 0
  const items = showResults
    ? (results ?? []).map(r => ({
        id: r.id,
        title: r.title,
        lastMessageAt: r.lastMessageAt,
        snippet: r.snippet,
      }))
    : sessions.map(s => ({
        id: s.id,
        title: s.title ?? null,
        lastMessageAt: s.lastMessageAt ?? null,
        snippet: null as string | null,
      }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Chat history</DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <div className="relative">
            <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search past conversations…"
              className="pl-9"
            />
          </div>

          <button
            type="button"
            onClick={onNewChat}
            className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border/60 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
            <span>New chat</span>
          </button>

          <div className="-mx-1 max-h-[420px] overflow-y-auto px-1">
            {showResults && isSearching && items.length === 0 && (
              <div className="py-8 text-center text-xs text-muted-foreground">
                Searching…
              </div>
            )}
            {showResults && !isSearching && items.length === 0 && (
              <div className="py-8 text-center text-xs text-muted-foreground">
                No matches for &ldquo;{query}&rdquo;.
              </div>
            )}
            {!showResults && items.length === 0 && (
              <div className="py-8 text-center text-xs text-muted-foreground">
                No saved conversations yet.
              </div>
            )}
            <ul className="divide-y divide-border/40">
              {items.map(item => {
                const isActive = item.id === activeSessionId
                const matched = sessions.find(s => s.id === item.id)
                return (
                  <li
                    key={item.id}
                    className={cn(
                      'group flex items-start gap-2 rounded-lg px-2 py-2 transition-colors',
                      'hover:bg-muted/50',
                      isActive && 'bg-muted/40',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        onSelectSession(
                          matched ?? {
                            id: item.id,
                            title: item.title,
                            lastMessageAt: item.lastMessageAt,
                            createdAt: '',
                            updatedAt: '',
                          },
                        )
                      }
                      className="flex min-w-0 flex-1 flex-col items-start text-left"
                    >
                      <span className="truncate text-sm font-medium text-foreground">
                        {item.title || 'Untitled chat'}
                      </span>
                      {item.snippet && (
                        <span className="mt-0.5 line-clamp-2 text-[12px] text-muted-foreground/90">
                          <SearchHighlight text={item.snippet} query={query} />
                        </span>
                      )}
                      {!item.snippet && item.lastMessageAt && (
                        <span className="mt-0.5 text-[11px] text-muted-foreground">
                          {new Date(item.lastMessageAt).toLocaleString()}
                        </span>
                      )}
                    </button>
                    {matched && (
                      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-full"
                          onClick={e => {
                            e.stopPropagation()
                            onRename(matched)
                          }}
                          aria-label="Rename"
                        >
                          <ArrowClockwise className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 rounded-full text-muted-foreground hover:text-red-500"
                          onClick={e => {
                            e.stopPropagation()
                            onDelete(matched)
                          }}
                          aria-label="Delete"
                        >
                          <Trash className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Render a search snippet with the matched substring bolded.
 */
function SearchHighlight({ text, query }: { text: string; query: string }) {
  const q = query.trim()
  if (!q) return <>{text}</>
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-sm bg-primary/20 px-0.5 text-foreground">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  )
}

function BennyDockedPanel({
  expanded,
  children,
}: {
  expanded: boolean
  children: React.ReactNode
}) {
  return (
    <aside
      aria-label="Benny assistant"
      data-expanded={expanded ? 'true' : 'false'}
      className={cn(
        'benny-docked-panel flex min-w-[380px] shrink-0 flex-col overflow-hidden border-l border-border/50 bg-background',
        'transition-[width] duration-200 ease-out animate-in fade-in-0 slide-in-from-right-2',
        expanded ? 'w-[min(38rem,48vw)]' : 'w-[clamp(25rem,31vw,29rem)]',
      )}
      style={{
        marginTop: 'var(--tauri-shell-offset, 0px)',
        height: 'calc(100% - var(--tauri-shell-offset, 0px))',
      }}
    >
      {children}
    </aside>
  )
}

function BennyChatInner({
  mode,
  messages,
  isLoading,
  isHistoryLoading,
  input,
  setInput,
  handleSend,
  onExpand,
  onCollapse,
  onClose,
  scrollRef,
  inputRef,
  isStreaming,
  onStop,
  isMobile,
  onNewChat,
  attachments,
  onAddFiles,
  onRemoveAttachment,
  isDropping,
  onSetDropping,
  onOpenSessions,
  onOpenHelpCitation,
  showEnableBennyHint,
  actionPlan,
  isPlanning,
  isExecutingPlan,
  onExecutePlan,
  onDismissPlan,
  onEditPlan,
  hasOlderMessages,
  isLoadingOlderMessages,
  onLoadOlderMessages,
  onHeaderPointerDown,
  isDailyLimitReached,
}: {
  mode: 'popover' | 'sheet' | 'companion'
  messages: AssistantChatMessage[]
  isLoading: boolean
  isHistoryLoading: boolean
  input: string
  setInput: (v: string) => void
  handleSend: (msg: string) => void
  onExpand: () => void
  onCollapse: () => void
  onClose: () => void
  scrollRef: React.RefObject<HTMLDivElement | null>
  inputRef: React.RefObject<HTMLInputElement | null>
  isStreaming: boolean
  onStop: () => void
  isMobile: boolean
  onNewChat: () => void
  attachments: PendingAttachment[]
  onAddFiles: (files: File[] | FileList) => void
  onRemoveAttachment: (id: string) => void
  isDropping: boolean
  onSetDropping: (dropping: boolean) => void
  onOpenSessions: () => void
  onOpenHelpCitation: (helpId: string) => void
  showEnableBennyHint: boolean
  actionPlan: AssistantActionPlan | null
  isPlanning: boolean
  isExecutingPlan: boolean
  onExecutePlan: () => void
  onDismissPlan: () => void
  onEditPlan: (instruction: string) => void
  hasOlderMessages: boolean
  isLoadingOlderMessages: boolean
  onLoadOlderMessages: () => void
  onHeaderPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void
  isDailyLimitReached: boolean
}) {
  const isPopover = mode === 'popover'
  const isCompanionMode = mode === 'companion'
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Drag-drop wiring on the whole chat container so users can drop anywhere.
  const dragHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes('Files')) {
        e.preventDefault()
        onSetDropping(true)
      }
    },
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes('Files')) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }
    },
    onDragLeave: (e: React.DragEvent) => {
      // Only fire when we leave the outer container, not when crossing
      // child boundaries. relatedTarget being outside the current box is
      // a reliable signal.
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        onSetDropping(false)
      }
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      onSetDropping(false)
      if (e.dataTransfer.files.length > 0) {
        onAddFiles(e.dataTransfer.files)
      }
    },
  }

  return (
    <div className="relative flex h-full flex-col" {...dragHandlers}>
      {/* Drag-drop overlay */}
      {isDropping && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-primary/10 backdrop-blur-[2px]">
          <div className="rounded-2xl border-2 border-dashed border-primary/60 bg-background/90 px-6 py-4 text-sm font-medium text-primary shadow-lg">
            Drop image to attach
          </div>
        </div>
      )}
      {/* Companion window: a chromeless top — no title bar, just a floating
          drag strip with the history + close controls. The dock tabs below
          already say which view you're in, so a titled header is redundant. */}
      {isCompanionMode ? (
        <div
          className="flex cursor-grab select-none items-center justify-end gap-1 px-2.5 pb-1 pt-2.5 active:cursor-grabbing"
          onPointerDown={onHeaderPointerDown}
        >
          <button
            type="button"
            onClick={onOpenSessions}
            aria-label="Chat history"
            title="Chat history"
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ChatCircleDots className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full bg-muted/50 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" weight="bold" />
          </button>
        </div>
      ) : (
        /* Header — taller + safe-area top inset on mobile. */
        <div
          className={cn(
            'border-b border-border/40 bg-background px-4 py-2.5',
            isPopover ? 'rounded-t-xl' : 'rounded-t-xl',
            isMobile && 'benny-mobile-header',
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'bg-purple-hen flex items-center justify-center overflow-hidden rounded-full',
                  isMobile ? 'h-8 w-8' : 'h-7 w-7',
                )}
                style={{
                  backgroundImage: 'url(/characters/Default.svg)',
                  backgroundSize: '140%',
                  backgroundPosition: 'center 10%',
                  backgroundRepeat: 'no-repeat',
                }}
              />
              <span
                className={cn(
                  'font-semibold tracking-tight text-foreground',
                  isMobile ? 'text-[15px]' : 'text-[14px]',
                )}
              >
                Benny
              </span>
              <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                Beta
              </span>
            </div>
            <div className="flex items-center gap-0.5">
              {!isCompanionMode && (
                <kbd className="mr-1 hidden items-center gap-0.5 rounded bg-muted/60 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
                  {getModifierKeyLabel()}.
                </kbd>
              )}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'rounded-full text-muted-foreground hover:text-foreground',
                  isMobile ? 'h-9 w-9' : 'h-7 w-7',
                )}
                onClick={onOpenSessions}
                aria-label="Chat history"
                title="Chat history"
              >
                <ChatCircleDots
                  className={isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'}
                />
              </Button>
              {/* Expand/collapse only makes sense on desktop, and not in the
                companion window (which owns its own sizing). */}
              {!isMobile && !isCompanionMode && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground"
                  onClick={isPopover ? onExpand : onCollapse}
                  aria-label={isPopover ? 'Expand' : 'Collapse'}
                >
                  {isPopover ? (
                    <ArrowsOutSimple className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowsInSimple className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'rounded-full text-muted-foreground hover:text-foreground',
                  isMobile ? 'h-9 w-9' : 'h-7 w-7',
                )}
                onClick={onClose}
                aria-label="Close"
              >
                <span
                  className={cn(
                    'leading-none',
                    isMobile ? 'text-2xl' : 'text-lg',
                  )}
                >
                  &times;
                </span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <ScrollArea
        className="benny-chat-scroll-area flex-1"
        viewportRef={scrollRef}
      >
        {isHistoryLoading ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="flex gap-1">
                <span className="benny-typing-dot" />
                <span className="benny-typing-dot" />
                <span className="benny-typing-dot" />
              </div>
              <span>Loading conversation...</span>
            </div>
          </div>
        ) : (
          <div className="flex min-h-full flex-col">
            {(isPlanning || actionPlan) && (
              <ActionPlanCard
                plan={actionPlan}
                loading={isPlanning}
                executing={isExecutingPlan}
                onExecute={onExecutePlan}
                onDismiss={onDismissPlan}
                onEdit={onEditPlan}
              />
            )}
            {messages.length === 0 ? (
              <EmptyState
                onSend={handleSend}
                compact={isPopover}
                showEnableBennyHint={showEnableBennyHint}
              />
            ) : (
              <LatestExchangeView
                messages={messages}
                isLoading={isLoading}
                onRetry={handleSend}
                onOpenHelpCitation={onOpenHelpCitation}
                hasOlderMessages={hasOlderMessages}
                isLoadingOlderMessages={isLoadingOlderMessages}
                onLoadOlderMessages={onLoadOlderMessages}
              />
            )}
          </div>
        )}
      </ScrollArea>

      {/* Input bar — always at the bottom. On mobile, padding includes the
          home-indicator safe-area inset and the input itself uses
          16px font-size to suppress iOS Safari's auto-zoom on focus. */}
      <div
        className={cn(
          'border-t border-border/40 bg-background/80 backdrop-blur-sm',
          isPopover ? 'px-3 py-2.5' : 'px-5 py-3.5',
          isMobile && 'benny-mobile-footer',
        )}
      >
        <div className="benny-input-wrapper relative">
          {/* Attachment previews */}
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {attachments.map(att => (
                <div
                  key={att.id}
                  className="group relative h-14 w-14 overflow-hidden rounded-lg border border-border/40 bg-muted"
                >
                  <img
                    src={att.previewUrl}
                    alt={att.fileName ?? 'attachment'}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(att.id)}
                    className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-background/90 text-foreground/70 opacity-90 shadow transition-opacity hover:opacity-100"
                    aria-label={`Remove ${att.fileName ?? 'attachment'}`}
                  >
                    <X weight="bold" className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Hidden file input — opened by the paperclip button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => {
              if (e.target.files) onAddFiles(e.target.files)
              // Reset so re-selecting the same file fires onChange again.
              e.target.value = ''
            }}
          />
          {/* Slash-command menu opens above the input when text starts with `/` */}
          {input.startsWith('/') && (
            <SlashCommandMenu
              filter={input.split(/\s/)[0] ?? ''}
              isMobile={isMobile}
              onSelect={cmd => {
                if (cmd.kind === 'expand') {
                  setInput(cmd.prompt)
                  // Re-focus so the user can hit Enter or edit.
                  setTimeout(() => inputRef.current?.focus(), 0)
                } else if (cmd.kind === 'action' && cmd.action === 'new_chat') {
                  setInput('')
                  onNewChat()
                  setTimeout(() => inputRef.current?.focus(), 0)
                }
              }}
            />
          )}
          <div
            className={cn(
              'benny-input-inner flex items-center gap-2',
              isMobile ? 'px-3 py-2.5' : 'px-3 py-2',
            )}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isDailyLimitReached}
              className={cn(
                'shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground',
                isMobile
                  ? 'flex h-9 w-9 items-center justify-center'
                  : 'flex h-7 w-7 items-center justify-center',
              )}
              aria-label="Attach image"
              title="Attach image"
            >
              <Paperclip className={isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={
                isDailyLimitReached
                  ? 'Daily limit reached'
                  : attachments.length > 0
                    ? 'Add a message…'
                    : 'Ask anything'
              }
              disabled={isDailyLimitReached}
              className={cn(
                'flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground/60',
                // 16px is the minimum size that disables iOS auto-zoom-on-focus.
                isMobile ? 'text-[16px]' : 'text-sm',
              )}
              onPaste={e => {
                // Pull pasted images straight into the attachments tray;
                // text paste behaves normally.
                const pasted: File[] = []
                for (const item of Array.from(e.clipboardData.items)) {
                  if (item.kind === 'file') {
                    const f = item.getAsFile()
                    if (f) pasted.push(f)
                  }
                }
                if (pasted.length > 0) {
                  e.preventDefault()
                  onAddFiles(pasted)
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  // If the user typed a slash command and hits Enter, run
                  // the first match instead of sending the literal text.
                  if (input.startsWith('/')) {
                    const filter = input.split(/\s/)[0] ?? ''
                    const exact = SLASH_COMMANDS.find(c => c.cmd === filter)
                    if (exact) {
                      if (exact.kind === 'expand') {
                        if (exact.cmd === '/plan') {
                          setInput(exact.prompt)
                        } else {
                          void handleSend(exact.prompt)
                        }
                      } else if (exact.action === 'new_chat') {
                        setInput('')
                        onNewChat()
                      }
                      return
                    }
                  }
                  void handleSend(input)
                }
              }}
            />
            {isStreaming ? (
              <Button
                size="icon"
                className={cn(
                  'shrink-0 rounded-full bg-foreground/90 text-background shadow-sm transition-all hover:bg-foreground',
                  isMobile ? 'h-10 w-10' : 'h-7 w-7',
                )}
                onClick={onStop}
                aria-label="Stop response"
              >
                <Stop
                  weight="fill"
                  className={isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'}
                />
              </Button>
            ) : (
              <Button
                size="icon"
                className={cn(
                  'shrink-0 rounded-full transition-all duration-200',
                  isMobile ? 'h-10 w-10' : 'h-7 w-7',
                  // Allow sending with attachments even if the text input is empty.
                  input.trim() || attachments.length > 0
                    ? 'scale-100 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                    : 'scale-95 bg-muted/50 text-muted-foreground opacity-60',
                )}
                disabled={
                  (!input.trim() && attachments.length === 0) ||
                  isLoading ||
                  isDailyLimitReached
                }
                onClick={() =>
                  handleSend(
                    input.trim() || (attachments.length > 0 ? '(image)' : ''),
                  )
                }
                aria-label="Send message"
              >
                <PaperPlaneRight
                  weight="fill"
                  className={isMobile ? 'h-4 w-4' : 'h-3.5 w-3.5'}
                />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ActionPlanCard({
  plan,
  loading,
  executing,
  onExecute,
  onDismiss,
  onEdit,
}: {
  plan: AssistantActionPlan | null
  loading: boolean
  executing: boolean
  onExecute: () => void
  onDismiss: () => void
  onEdit: (instruction: string) => void
}) {
  if (loading && !plan) {
    return (
      <section className="border-b border-border/40 bg-card/40 px-4 py-4">
        <div className="mx-auto max-w-[520px] space-y-3 rounded-2xl border border-border/50 bg-background p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold text-foreground/70">
            <span className="benny-tool-spinner" aria-hidden="true" />
            Building a permission-safe plan…
          </div>
          <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
          <div className="h-3 w-3/5 animate-pulse rounded bg-muted/70" />
        </div>
      </section>
    )
  }
  if (!plan) return null

  const finished = plan.status === 'completed'
  const failed = plan.status === 'failed'

  return (
    <section className="border-b border-border/40 bg-[linear-gradient(135deg,hsl(var(--card)),hsl(var(--muted)/0.3))] px-4 py-4">
      <div className="mx-auto max-w-[540px] overflow-hidden rounded-2xl border border-border/55 bg-background shadow-[0_14px_40px_-28px_hsl(var(--foreground)/0.45)]">
        <div className="flex items-start justify-between border-b border-border/40 px-4 py-3.5">
          <div className="min-w-0 pr-3">
            <div className="mb-1 flex items-center gap-2">
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  finished
                    ? 'bg-emerald-500'
                    : failed
                      ? 'bg-red-500'
                      : 'bg-sky-500',
                )}
              />
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Review before running
              </span>
            </div>
            <h3 className="truncate text-sm font-semibold text-foreground">
              {plan.title}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {plan.summary}
            </p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground/70 hover:bg-muted hover:text-foreground"
            aria-label="Dismiss action plan"
          >
            <X className="h-3.5 w-3.5" weight="bold" />
          </button>
        </div>

        <ol className="divide-y divide-border/35 px-4">
          {plan.steps.map((step, index) => (
            <li key={step.id} className="flex gap-3 py-3">
              <span
                className={cn(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  step.permission === 'blocked'
                    ? 'bg-red-500/10 text-red-600'
                    : 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                )}
              >
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-xs font-medium leading-relaxed text-foreground/90">
                    {step.description}
                  </p>
                  <span className="shrink-0 rounded-full bg-muted/70 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {step.risk} risk
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                  {step.blockedReason ?? step.expectedOutcome}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {plan.questions.length > 0 && (
          <div className="mx-4 mb-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
              Benny needs your input
            </p>
            {plan.questions.map(question => (
              <p
                key={question}
                className="mt-1 text-xs text-amber-900/80 dark:text-amber-100/80"
              >
                {question}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-col items-stretch gap-2 border-t border-border/40 bg-muted/20 px-4 py-3">
          <p className="min-w-0 flex-1 text-[10px] leading-relaxed text-muted-foreground">
            Nothing runs until you approve. Permissions are checked again at
            execution.
          </p>
          <div className="flex shrink-0 items-center justify-end gap-2">
            {!finished && !failed && (
              <button
                type="button"
                onClick={() => onEdit(plan.instruction)}
                disabled={executing}
                className="rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                Edit request
              </button>
            )}
            <Button
              size="sm"
              onClick={onExecute}
              disabled={!plan.executable || executing || finished || failed}
              className={cn(
                'h-8 rounded-full px-3.5 text-xs shadow-sm',
                finished && 'bg-emerald-600 hover:bg-emerald-600',
              )}
            >
              {executing ? (
                <>
                  <span className="benny-tool-spinner mr-1.5" /> Running…
                </>
              ) : finished ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Completed
                </>
              ) : failed ? (
                'Stopped'
              ) : (
                'Approve & run'
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

function EmptyState({
  onSend,
  compact = false,
  showEnableBennyHint = false,
}: {
  onSend: (msg: string) => void
  compact?: boolean
  showEnableBennyHint?: boolean
}) {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const setBennyOpen = useBennyStore(s => s.setOpen)
  const helpCenterPath = '/help'
  const preferencesPath = orgSlug
    ? `/${orgSlug}/user-settings#preferences`
    : '/user-settings#preferences'

  return (
    <div
      className={cn(
        'flex min-h-full flex-col items-center pt-8',
        compact ? 'px-4' : 'px-8',
      )}
    >
      {/* Spacer — pushes greeting to center */}
      <div className="flex-1" />

      {/* Greeting */}
      <div className="benny-greeting text-center">
        <h2
          className={cn(
            'font-bold tracking-tight text-foreground',
            compact ? 'text-lg' : 'text-2xl',
          )}
        >
          What can I do for you today?
        </h2>
        {showEnableBennyHint && (
          <div className="border-purple-hen/20 bg-purple-hen/5 mx-auto mt-3 flex max-w-[340px] items-start gap-2 rounded-lg border px-3 py-2 text-left text-xs leading-relaxed text-muted-foreground">
            <WarningCircle className="text-purple-hen mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Enable Benny in{' '}
              <AssistantAppLink
                to={preferencesPath}
                onClick={() => setBennyOpen(false)}
                className="text-purple-hen hover:text-purple-hen/80 font-medium underline underline-offset-2"
              >
                Settings &gt; Preferences
              </AssistantAppLink>{' '}
              to show the assistant button throughout Scrambl.
            </p>
          </div>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Quick prompts */}
      <div
        className="benny-greeting w-full max-w-[420px] space-y-0.5 pb-4"
        style={{ animationDelay: '0.25s' }}
      >
        {QUICK_PROMPTS.map(({ icon: Icon, text }) => (
          <button
            key={text}
            className="benny-prompt-item flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted/40"
            onClick={() => onSend(text)}
          >
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            <span>{text}</span>
          </button>
        ))}
        <AssistantAppLink
          to={helpCenterPath}
          className="benny-prompt-item mt-2 flex w-full items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/25 px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:border-border hover:bg-muted/50 hover:text-foreground"
          onClick={() => setBennyOpen(false)}
        >
          <span className="flex min-w-0 items-center gap-3">
            <BookOpenText className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            <span>Browse the Help Center</span>
          </span>
          <ArrowSquareOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
        </AssistantAppLink>
      </div>
    </div>
  )
}

/** Codes whose failure is shown as an error card inside the thread. */
function isInThreadErrorCode(code?: string): boolean {
  return !!code && code in ERROR_PRESENTATION
}

/**
 * Legacy prefixes for turns persisted before the server stamped an
 * `errorCode`. Matched at the start only — a healthy answer that happens to
 * say "try again" mid-sentence must not render as a failure.
 */
const LEGACY_ERROR_PREFIXES = [
  'I ran into an issue',
  'AI provider request failed',
  'AI rate limit reached',
]

function isErrorMessage(message: AssistantChatMessage): boolean {
  if (message.role !== 'assistant') return false
  if (message.metadata?.error || message.metadata?.errorCode) return true
  const content = (message.content || '').trimStart()
  return LEGACY_ERROR_PREFIXES.some(prefix => content.startsWith(prefix))
}

/**
 * How a failed turn presents itself. `canRetry` is the important bit: an
 * unauthorized or missing model can't be retried into working, so offering
 * "Try again" there just walks the user into the same wall. Those cases get
 * the Help Center as the way forward instead.
 */
const ERROR_PRESENTATION: Record<string, { title: string; canRetry: boolean }> =
  {
    BENNY_PROVIDER_UNAVAILABLE: {
      title: 'Benny is unavailable',
      canRetry: false,
    },
    BENNY_RATE_LIMITED: { title: 'Benny is rate limited', canRetry: true },
    BENNY_PROVIDER_TIMEOUT: { title: 'Benny timed out', canRetry: true },
    BENNY_PROVIDER_ERROR: {
      title: "Benny couldn't answer that",
      canRetry: true,
    },
    BENNY_TOOL_LOOP_EXHAUSTED: {
      title: 'Benny got stuck on that one',
      canRetry: true,
    },
  }

function getErrorPresentation(message: AssistantChatMessage) {
  const code =
    typeof message.metadata?.errorCode === 'string'
      ? message.metadata.errorCode
      : undefined
  return (
    (code && ERROR_PRESENTATION[code]) || {
      title: "Benny couldn't answer that",
      canRetry: true,
    }
  )
}

const DOC_CITATION_PATTERN = /\[doc:([A-Za-z0-9._-]+)\]/g
const KNOWLEDGE_CITATION_PATTERN = /\[knowledge:([A-Za-z0-9-]+)\]/g

function extractDocCitationIds(content?: string | null) {
  if (!content) return []
  const ids = new Set<string>()
  for (const match of content.matchAll(DOC_CITATION_PATTERN)) {
    if (match[1]) ids.add(match[1])
  }
  return [...ids]
}

function linkDocCitations(content: string) {
  return content
    .replace(
      DOC_CITATION_PATTERN,
      (_match, id: string) => `[doc:${id}](scrambl-help://${id})`,
    )
    .replace(
      KNOWLEDGE_CITATION_PATTERN,
      (_match, id: string) => `[knowledge:${id}](scrambl-knowledge://${id})`,
    )
}

function getReactNodeText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) return node.map(getReactNodeText).join('')
  if (isValidElement<{ children?: React.ReactNode }>(node)) {
    return getReactNodeText(node.props.children)
  }
  return ''
}

const LatestExchangeView = memo(function LatestExchangeView({
  messages,
  isLoading,
  onRetry,
  onOpenHelpCitation,
  hasOlderMessages,
  isLoadingOlderMessages,
  onLoadOlderMessages,
}: {
  messages: AssistantChatMessage[]
  isLoading: boolean
  onRetry: (msg: string) => void
  onOpenHelpCitation: (helpId: string) => void
  hasOlderMessages: boolean
  isLoadingOlderMessages: boolean
  onLoadOlderMessages: () => void
}) {
  // Subscribe directly so pills update independently from prop changes —
  // this keeps the streaming animation smooth without re-rendering parents.
  const streamingState = useBennyStore(s => s.streamingState)
  const [showOlder, setShowOlder] = useState(false)

  // The typing dots must stay up continuously from send until the answer is
  // visible (or the turn fails) — never blink. Deriving visibility straight
  // from the stream makes it flicker: each tool-loop `message_start` resets
  // `content` to '' and clears the tool pills, and an answer that opens with
  // a scrambl-cards fence sanitizes to '' until the block closes. So latch
  // once real content renders, and reset when the turn ends.
  const hasVisibleOutput =
    !!streamingState && sanitizeStreamingContent(streamingState.content) !== ''
  const [hasTurnOutput, setHasTurnOutput] = useState(false)
  useEffect(() => {
    if (!isLoading) setHasTurnOutput(false)
  }, [isLoading])
  useEffect(() => {
    if (isLoading && hasVisibleOutput) setHasTurnOutput(true)
  }, [isLoading, hasVisibleOutput])
  const showTypingIndicator = isLoading && !hasTurnOutput

  // Filter out tool messages and empty assistant messages
  const visible = messages.filter(msg => {
    if (msg.role === 'tool') return false
    if (msg.role === 'assistant' && !msg.content?.trim()) return false
    return true
  })

  // Find the last 3 user message indices to show 3 exchanges
  const userIndices: number[] = []
  for (let i = visible.length - 1; i >= 0 && userIndices.length < 3; i--) {
    if (visible[i].role === 'user') userIndices.push(i)
  }

  const cutoff =
    userIndices.length > 0
      ? userIndices[userIndices.length - 1]
      : Math.max(0, visible.length - 1)
  const recentExchanges = visible.slice(cutoff)
  const olderMessages = cutoff > 0 ? visible.slice(0, cutoff) : []

  return (
    <div className="flex min-h-full min-w-0 flex-col px-3 py-3">
      <div className="mt-auto min-w-0 space-y-3">
        {/* Collapsed older messages toggle */}
        {(olderMessages.length > 0 || hasOlderMessages) && (
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                if (!showOlder) {
                  setShowOlder(true)
                } else if (hasOlderMessages) {
                  onLoadOlderMessages()
                } else {
                  setShowOlder(false)
                }
              }}
              disabled={isLoadingOlderMessages}
              className={cn(
                'text-[11px] font-medium text-muted-foreground',
                'rounded-full px-3 py-1 transition-colors',
                'hover:bg-muted/40',
                'disabled:pointer-events-none disabled:opacity-50',
              )}
            >
              {isLoadingOlderMessages
                ? 'Loading older messages…'
                : !showOlder
                  ? `Show ${olderMessages.length}${hasOlderMessages ? '+' : ''} earlier message${olderMessages.length === 1 && !hasOlderMessages ? '' : 's'}`
                  : hasOlderMessages
                    ? 'Load older messages'
                    : 'Hide older messages'}
            </button>
            {showOlder && hasOlderMessages && (
              <button
                type="button"
                onClick={() => setShowOlder(false)}
                className="rounded-full px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted/40"
              >
                Hide
              </button>
            )}
          </div>
        )}

        {/* Older messages (collapsed by default) */}
        {showOlder &&
          olderMessages.map(message => (
            <div key={message.id} className="opacity-50">
              <MessageBubble
                message={message}
                isLatest={false}
                onOpenHelpCitation={onOpenHelpCitation}
              />
            </div>
          ))}

        {/* Separator between old and latest */}
        {showOlder && olderMessages.length > 0 && (
          <div className="my-2 border-t border-border/30" />
        )}

        {/* Recent exchanges */}
        {recentExchanges.map((message, index) => {
          // For an errored assistant message, surface a Retry chip pointing
          // back at the original user prompt.
          let retryMessage: string | undefined
          // For any assistant message, surface a Regenerate chip pointing
          // back at the user prompt above it.
          let regeneratePrompt: string | undefined

          if (message.role === 'assistant') {
            const allVisible = visible
            const msgIndex = allVisible.findIndex(m => m.id === message.id)
            for (let i = msgIndex - 1; i >= 0; i--) {
              if (allVisible[i].role === 'user' && allVisible[i].content) {
                regeneratePrompt = allVisible[i].content!
                if (isErrorMessage(message)) {
                  retryMessage = regeneratePrompt
                }
                break
              }
            }
          }
          // Render tool-call pills above the streaming assistant bubble.
          const isStreamingThisMessage =
            streamingState?.messageId === message.id
          return (
            <div key={message.id}>
              {isStreamingThisMessage &&
                streamingState.toolCalls.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap gap-1.5 pl-8">
                    {streamingState.toolCalls.map(tc => (
                      <ToolCallPill key={tc.toolCallId} tool={tc} />
                    ))}
                  </div>
                )}
              <MessageBubble
                message={message}
                isLatest={index === recentExchanges.length - 1}
                retryMessage={retryMessage}
                onRetry={onRetry}
                isStreaming={isStreamingThisMessage}
                regeneratePrompt={regeneratePrompt}
                onRegenerate={onRetry}
                onSend={onRetry}
                onOpenHelpCitation={onOpenHelpCitation}
              />
            </div>
          )
        })}
        {/* One continuous indicator for the whole turn: it appears on send and
            stays up (alongside any tool-call pills) until the reply actually
            renders or the turn ends. See `showTypingIndicator` above. */}
        {showTypingIndicator && <TypingIndicator />}
      </div>
    </div>
  )
})
LatestExchangeView.displayName = 'LatestExchangeView'

function TypingIndicator() {
  return (
    <div className="benny-msg-enter flex justify-start">
      <div className="flex items-center gap-1.5 rounded-2xl border border-border/40 bg-card px-4 py-3 shadow-sm">
        <span className="benny-typing-dot" />
        <span className="benny-typing-dot" />
        <span className="benny-typing-dot" />
      </div>
    </div>
  )
}

/**
 * Inline pill rendered above the streaming assistant bubble while a tool call
 * runs. Compact and animated — the user sees what Benny is doing in real time
 * instead of waiting for a final blob.
 */
function ToolCallPill({ tool }: { tool: BennyToolCallState }) {
  const label = humanizeToolName(tool.toolName)
  return (
    <div
      className={cn(
        'benny-tool-pill flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium',
        tool.status === 'working' &&
          'border-primary/30 bg-primary/5 text-primary',
        tool.status === 'completed' &&
          'border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400',
        tool.status === 'failed' &&
          'border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400',
      )}
    >
      {tool.status === 'working' && (
        <span className="benny-tool-spinner" aria-hidden="true" />
      )}
      {tool.status === 'completed' && <Check className="h-3 w-3" />}
      {tool.status === 'failed' && <WarningCircle className="h-3 w-3" />}
      <span>
        {tool.status === 'working'
          ? label
          : tool.status === 'failed'
            ? `${label} failed`
            : label}
      </span>
      {tool.status === 'completed' && tool.durationMs !== undefined && (
        <span className="text-muted-foreground">
          {formatDuration(tool.durationMs)}
        </span>
      )}
    </div>
  )
}

/**
 * Render parsed scrambl-followups as a row of clickable chips. Clicking a
 * chip immediately sends the prompt as if the user typed it.
 */
/**
 * Built-in slash commands. `expand` types out a prompt for the user;
 * `action` runs a side-effect (new chat, etc.). New entries are showable in
 * the slash menu the moment they're added — no router changes needed.
 */
type SlashCommand =
  | {
      cmd: string
      label: string
      description: string
      kind: 'expand'
      prompt: string
    }
  | {
      cmd: string
      label: string
      description: string
      kind: 'action'
      action: 'new_chat'
    }

const SLASH_COMMANDS: readonly SlashCommand[] = [
  {
    cmd: '/plan',
    label: 'Plan multiple actions',
    description: 'Preview permission-checked changes before running anything.',
    kind: 'expand',
    prompt: 'Plan: ',
  },
  {
    cmd: '/summarize',
    label: 'Summarize this page',
    description: "Get a 1-2 sentence overview of what you're looking at.",
    kind: 'expand',
    prompt: 'Summarize this page in 1-2 sentences.',
  },
  {
    cmd: '/tasks',
    label: 'Show my tasks',
    description: 'Top priorities for today.',
    kind: 'expand',
    prompt: 'Show my top priorities for today.',
  },
  {
    cmd: '/overdue',
    label: 'Show overdue',
    description: 'Tasks past their due date.',
    kind: 'expand',
    prompt: 'Show me my overdue tasks.',
  },
  {
    cmd: '/help',
    label: 'What can you do?',
    description: "List Benny's capabilities.",
    kind: 'expand',
    prompt: 'What can you do?',
  },
  {
    cmd: '/new',
    label: 'New chat',
    description: 'Start a fresh conversation.',
    kind: 'action',
    action: 'new_chat',
  },
] as const

function SlashCommandMenu({
  filter,
  onSelect,
  isMobile,
}: {
  filter: string
  onSelect: (command: SlashCommand) => void
  isMobile: boolean
}) {
  const matches = SLASH_COMMANDS.filter(c =>
    c.cmd.startsWith(filter.toLowerCase()),
  )
  const [activeIndex, setActiveIndex] = useState(0)

  // Reset highlight whenever the filtered list changes shape.
  useEffect(() => {
    setActiveIndex(0)
  }, [matches.length])

  // Keyboard nav: arrow keys + Enter. We hook into the document so the
  // menu can react while the input still owns focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (matches.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => (i + 1) % matches.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => (i - 1 + matches.length) % matches.length)
      } else if (e.key === 'Tab') {
        e.preventDefault()
        const cmd = matches[activeIndex]
        if (cmd) onSelect(cmd)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [matches, activeIndex, onSelect])

  if (matches.length === 0) return null

  return (
    <div
      className={cn(
        'absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-xl border border-border/40 bg-popover shadow-lg',
        'duration-150 animate-in fade-in-0 slide-in-from-bottom-1',
      )}
    >
      <div className="max-h-[260px] overflow-y-auto py-1">
        {matches.map((cmd, idx) => (
          <button
            key={cmd.cmd}
            type="button"
            // Use mousedown to fire before the input loses focus.
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
              onSelect(cmd)
            }}
            onMouseEnter={() => setActiveIndex(idx)}
            className={cn(
              'grid w-full grid-cols-[6.5rem_minmax(0,1fr)] items-start gap-3 px-3 text-left transition-colors',
              isMobile ? 'py-2.5' : 'py-2',
              idx === activeIndex
                ? 'bg-accent/60 text-accent-foreground'
                : 'text-foreground hover:bg-accent/40',
            )}
          >
            <span className="mt-0.5 w-fit rounded-md bg-muted/60 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
              {cmd.cmd}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium leading-tight">
                {cmd.label}
              </span>
              <span className="mt-0.5 block truncate text-[11px] leading-tight text-muted-foreground">
                {cmd.description}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function FollowupChips({
  prompts,
  onSend,
}: {
  prompts: string[]
  onSend: (msg: string) => void
}) {
  if (prompts.length === 0) return null
  return (
    <div className="benny-followups mt-2 flex flex-wrap gap-1.5">
      {prompts.map(prompt => (
        <button
          key={prompt}
          type="button"
          onClick={() => onSend(prompt)}
          className={cn(
            'rounded-full border border-border/40 bg-background px-2.5 py-1 text-[12px]',
            'text-muted-foreground transition-colors',
            'hover:border-border/70 hover:bg-muted/40 hover:text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring/60',
          )}
        >
          {prompt}
        </button>
      ))}
    </div>
  )
}

function humanizeToolName(name: string): string {
  // task_get → "Getting task", task_list → "Listing tasks", etc.
  const verbMap: Record<string, string> = {
    get: 'Getting',
    list: 'Listing',
    create: 'Creating',
    update: 'Updating',
    delete: 'Deleting',
    search: 'Searching',
    assign: 'Assigning',
    complete: 'Completing',
    archive: 'Archiving',
    convert: 'Converting',
  }
  const parts = name.split('_')
  const first = parts[0]
  const lastSegment = parts[parts.length - 1]
  const verb = verbMap[lastSegment]
  if (verb) {
    const subject = parts.slice(0, -1).join(' ')
    return `${verb} ${subject}…`
  }
  // Generic fallback — title-case the whole tool name.
  return name.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase())
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

const MessageBubble = memo(
  function MessageBubble({
    message,
    isLatest,
    retryMessage,
    onRetry,
    isStreaming,
    regeneratePrompt,
    onRegenerate,
    onSend,
    onOpenHelpCitation,
  }: {
    message: AssistantChatMessage
    isLatest: boolean
    retryMessage?: string
    onRetry?: (msg: string) => void
    isStreaming?: boolean
    regeneratePrompt?: string
    onRegenerate?: (msg: string) => void
    onSend?: (msg: string) => void
    onOpenHelpCitation?: (helpId: string) => void
  }) {
    const isUser = message.role === 'user'
    const isAssistant = message.role === 'assistant'
    const isTool = message.role === 'tool'
    const user = useAuth(s => s.user)
    const { orgSlug: routeOrgSlug = '' } = useParams<{ orgSlug: string }>()
    const { currentOrganization } = useOrganizationStore()
    const orgSlug = routeOrgSlug || currentOrganization?.slug || ''
    const forwardNavigate = useAssistantNavigation()
    const [hasCopied, setHasCopied] = useState(false)
    const docCitationIds = useMemo(
      () => extractDocCitationIds(message.content),
      [message.content],
    )
    const markdownContent = useMemo(
      () => linkDocCitations(message.content || (isStreaming ? '' : '...')),
      [isStreaming, message.content],
    )

    // Stable components map for ReactMarkdown. If this object is rebuilt every
    // render with fresh function refs, ReactMarkdown treats `pre`/`code`/`a` as
    // new component types and unmounts/remounts their subtrees — which makes
    // <ModuleCardList> replay its framer-motion entrance animation on every
    // unrelated re-render of this bubble (e.g. while typing in the input).
    const markdownComponents = useMemo(
      () => ({
        a({ href, children, ...props }: any) {
          if (href) {
            if (href.startsWith('scrambl-help://')) {
              const helpId = href.replace('scrambl-help://', '')
              return (
                <button
                  type="button"
                  onClick={() => onOpenHelpCitation?.(helpId)}
                  className="mx-0.5 inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-800 hover:bg-sky-100"
                >
                  {children}
                </button>
              )
            }
            if (href.startsWith('scrambl-knowledge://')) {
              const articleId = href.replace('scrambl-knowledge://', '')
              return (
                <AssistantAppLink
                  to={`/${orgSlug}/knowledge-base/read/${articleId}`}
                  className="mx-0.5 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                >
                  {children}
                </AssistantAppLink>
              )
            }
            const parsed = parseScramblLink(href)
            if (parsed) {
              return (
                <ModuleInlineChip type={parsed.type} id={parsed.id}>
                  {children}
                </ModuleInlineChip>
              )
            }
            if (href.startsWith('/') && forwardNavigate) {
              return (
                <AssistantAppLink
                  to={href}
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                  {...props}
                >
                  {children}
                </AssistantAppLink>
              )
            }
          }
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:text-primary/80"
              {...props}
            >
              {children}
            </a>
          )
        },
        pre({ children, ...props }: any) {
          const childArray = Array.isArray(children) ? children : [children]
          for (const child of childArray) {
            if (child && typeof child === 'object' && 'props' in child) {
              const codeProps = child.props as {
                className?: string
                children?: React.ReactNode
              }
              const text = getReactNodeText(codeProps.children)
              if (codeProps.className?.includes('language-scrambl-cards')) {
                const cards = parseScramblCards(text)
                if (cards && cards.length > 0) {
                  return (
                    <ModuleCardList
                      cards={normalizeAssistantCardsForContext(
                        cards,
                        message.content,
                      )}
                      animationKey={message.id}
                    />
                  )
                }
              }
              if (codeProps.className?.includes('language-scrambl-followups')) {
                const prompts = parseScramblFollowups(text)
                if (prompts && onSend) {
                  return <FollowupChips prompts={prompts} onSend={onSend} />
                }
                return null
              }
              // Canvas directives are for Simple Mode's canvas — invisible here.
              if (codeProps.className?.includes('language-scrambl-canvas')) {
                return null
              }
              // Composed view specs render as live panes on Simple Mode's
              // canvas — never as a code block in the thread.
              if (codeProps.className?.includes('language-scrambl-view')) {
                return null
              }
            }
          }
          return <pre {...props}>{children}</pre>
        },
        code({ className, children, ...props }: any) {
          if (className?.includes('language-scrambl-cards')) {
            const text = getReactNodeText(children)
            const cards = parseScramblCards(text)
            if (cards && cards.length > 0) {
              return (
                <ModuleCardList
                  cards={normalizeAssistantCardsForContext(
                    cards,
                    message.content,
                  )}
                  animationKey={message.id}
                />
              )
            }
          }
          if (className?.includes('language-scrambl-followups')) {
            const text = getReactNodeText(children)
            const prompts = parseScramblFollowups(text)
            if (prompts && onSend) {
              return <FollowupChips prompts={prompts} onSend={onSend} />
            }
            return null
          }
          // Canvas directives are for Simple Mode's canvas — invisible here.
          if (className?.includes('language-scrambl-canvas')) {
            return null
          }
          // Composed view specs render as live panes on Simple Mode's canvas —
          // never as a code block in the thread.
          if (className?.includes('language-scrambl-view')) {
            return null
          }
          const isBlock =
            className?.includes('language-') || className?.includes('hljs')
          if (!isBlock) {
            return (
              <code
                className={cn('assistant-inline-code', className)}
                {...props}
              >
                {children}
              </code>
            )
          }
          return (
            <code className={cn('assistant-code-block', className)} {...props}>
              {children}
            </code>
          )
        },
      }),
      [
        forwardNavigate,
        message.content,
        message.id,
        onOpenHelpCitation,
        onSend,
        orgSlug,
      ],
    )

    useEffect(() => {
      if (!hasCopied) return
      const timeout = setTimeout(() => setHasCopied(false), 1600)
      return () => clearTimeout(timeout)
    }, [hasCopied])

    if (isTool) {
      const rawContent = message.content?.trim()
      let formattedContent = message.content || 'No output.'

      if (rawContent) {
        const looksLikeJson =
          (rawContent.startsWith('{') && rawContent.endsWith('}')) ||
          (rawContent.startsWith('[') && rawContent.endsWith(']'))

        if (looksLikeJson) {
          try {
            formattedContent = JSON.stringify(JSON.parse(rawContent), null, 2)
          } catch (error) {
            formattedContent = message.content || 'No output.'
          }
        }
      }

      return (
        <Collapsible>
          <div
            className={cn(
              'rounded-xl border border-border/40 bg-muted/30 p-3 text-xs text-muted-foreground',
              isLatest && 'benny-msg-enter',
            )}
          >
            <CollapsibleTrigger className="flex w-full items-center justify-between text-left text-xs font-medium text-foreground/80">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary/60" />
                {message.toolName || 'tool'}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                details
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">
              {formattedContent}
            </CollapsibleContent>
          </div>
        </Collapsible>
      )
    }

    const handleCopy = async () => {
      if (!message.content) return
      // Copy human-readable text: card JSON becomes a bullet list, machine
      // blocks and deep-link URLs are stripped.
      const clipboardText = toClipboardText(message.content)
      if (!clipboardText) return
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(clipboardText)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = clipboardText
          textArea.style.position = 'fixed'
          textArea.style.left = '-9999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        setHasCopied(true)
      } catch (error) {
        toast.error('Unable to copy message.')
      }
    }

    // A failed turn is not a reply — render it as a failure the user can read
    // at a glance, with the one action that can actually move them forward.
    if (isAssistant && !isStreaming && isErrorMessage(message)) {
      const { title, canRetry } = getErrorPresentation(message)
      return (
        <div
          className={cn(
            'w-full min-w-0 max-w-full',
            isLatest && 'benny-msg-enter',
          )}
          role="alert"
        >
          <div className="ml-8 rounded-2xl border border-destructive/30 bg-destructive/5 px-3.5 py-3">
            <div className="flex items-start gap-2">
              <WarningCircle
                weight="fill"
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {message.content}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 pl-6">
              {canRetry && retryMessage && onRetry && (
                <button
                  type="button"
                  onClick={() => onRetry(retryMessage)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
                >
                  <ArrowClockwise className="h-3 w-3" />
                  <span>Try again</span>
                </button>
              )}
              <AssistantAppLink
                to={orgSlug ? `/${orgSlug}/help` : '/help'}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <BookOpenText className="h-3 w-3" />
                <span>Search the Help Center</span>
              </AssistantAppLink>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'w-full min-w-0 max-w-full',
          isAssistant && 'group',
          isLatest && 'benny-msg-enter',
        )}
      >
        <div
          className={cn(
            'flex w-full min-w-0 max-w-full items-end overflow-hidden',
            isUser ? 'justify-end gap-2 pr-1' : 'justify-start',
          )}
        >
          {isAssistant && (
            <div
              className="bg-purple-hen mr-2 h-6 w-6 shrink-0 overflow-hidden rounded-full"
              style={{
                backgroundImage: 'url(/characters/Default.svg)',
                backgroundSize: '140%',
                backgroundPosition: 'center 10%',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )}
          {isUser && (
            <div className="order-1 shrink-0">
              <UserAvatar user={user} size="xs" />
            </div>
          )}
          <div
            className={cn(
              'min-w-0 overflow-hidden break-words rounded-2xl px-3.5 py-3 text-sm',
              isAssistant ? 'flex-1' : 'max-w-[82%]',
              isUser
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'border border-border/40 bg-card text-foreground shadow-sm',
              isAssistant && 'leading-relaxed',
            )}
          >
            {isAssistant ? (
              <div className="assistant-markdown min-w-0 max-w-full overflow-hidden">
                <ReactMarkdown
                  rehypePlugins={rehypePlugins}
                  urlTransform={assistantUrlTransform}
                  components={markdownComponents}
                >
                  {markdownContent}
                </ReactMarkdown>
                {docCitationIds.length > 0 && (
                  <HelpCitationSources
                    ids={docCitationIds}
                    onOpen={onOpenHelpCitation}
                  />
                )}
                {isStreaming && (
                  <span className="benny-streaming-cursor" aria-hidden="true" />
                )}
              </div>
            ) : (
              message.content || '...'
            )}
            {isAssistant && (
              <div className="mt-2 flex items-center justify-end gap-1 border-t border-border/30 pt-2 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                {regeneratePrompt && onRegenerate && !isStreaming && (
                  <button
                    type="button"
                    onClick={() => onRegenerate(regeneratePrompt)}
                    className="flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Regenerate response"
                    title="Regenerate"
                  >
                    <ArrowClockwise className="h-3 w-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCopy}
                  className={cn(
                    'flex items-center gap-1 rounded-full px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                    hasCopied && 'text-emerald-600 opacity-100',
                  )}
                  aria-label="Copy message"
                >
                  {hasCopied ? (
                    <>
                      <Check className="h-3 w-3" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
  (prev, next) =>
    prev.message === next.message &&
    prev.isLatest === next.isLatest &&
    prev.retryMessage === next.retryMessage &&
    prev.onRetry === next.onRetry &&
    prev.isStreaming === next.isStreaming &&
    prev.regeneratePrompt === next.regeneratePrompt &&
    prev.onRegenerate === next.onRegenerate &&
    prev.onSend === next.onSend &&
    prev.onOpenHelpCitation === next.onOpenHelpCitation,
)

function HelpCitationSources({
  ids,
  onOpen,
}: {
  ids: string[]
  onOpen?: (helpId: string) => void
}) {
  const { isEntryVisible, flagsReady } = useHelpVisibility()
  const sourceQueries = useQueries({
    queries: ids.map(id => ({
      queryKey: ['help-entry', id],
      queryFn: () => helpApi.get(id),
      staleTime: 5 * 60 * 1000,
    })),
  })

  // Drop citations for feature-off modules so a personal account never gets a
  // Tickets/Invoices source chip that would only redirect to the help home.
  const visibleIds = ids.filter((_, index) => {
    const entry = sourceQueries[index]?.data
    if (!entry) return true
    return !flagsReady || isEntryVisible(entry)
  })

  if (visibleIds.length === 0) return null

  return (
    <div className="mt-3 border-t border-border/40 pt-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Sources
      </div>
      <div className="space-y-1.5">
        {visibleIds.map(id => {
          const entry = sourceQueries[ids.indexOf(id)]?.data
          return (
            <div
              key={id}
              className="flex w-full items-center gap-2 rounded-md border border-border/50 bg-muted/20 px-2.5 py-2"
            >
              <button
                type="button"
                onClick={() => onOpen?.(id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium">
                    {entry?.title ?? id}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {entry?.moduleSlug ?? 'help'}
                  </span>
                </span>
              </button>
              {entry ? (
                <HelpPlanBadge plans={entry.featurePlans} />
              ) : (
                <Badge variant="outline">doc</Badge>
              )}
              <AssistantAppLink
                to={`/help/articles/${encodeURIComponent(id)}`}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={`Open ${entry?.title ?? id} as article`}
              >
                <ArrowSquareOut className="h-3.5 w-3.5" />
              </AssistantAppLink>
            </div>
          )
        })}
      </div>
    </div>
  )
}

MessageBubble.displayName = 'MessageBubble'
